import { useState, useRef } from "react";
import { X, Camera, MapPin, Upload, Loader2 } from "lucide-react";
import { supabase } from "../lib/supabaseClient.ts";
import type { User } from "../types.ts";

interface QuickReportModalProps {
  open: boolean;
  onClose: () => void;
  user: User;
  onReportCreated: () => void;
}

export default function QuickReportModal({ open, onClose, user, onReportCreated }: QuickReportModalProps) {
  const [formData, setFormData] = useState({
    village: "",
    crop: "maize",
    reportType: "pest" as "pest" | "disease" | "yield" | "weather" | "technique",
    severity: "medium" as "low" | "medium" | "high" | "critical",
    description: ""
  });
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>("");
  const [gpsCoords, setGpsCoords] = useState<{lat: number, lng: number} | null>(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGpsCoords({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => console.warn("Location access denied:", error)
      );
    }
  };

  const uploadPhoto = async (file: File): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `community-reports/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('community-photos')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('community-photos')
        .getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error) {
      console.error('Photo upload failed:', error);
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      let photoUrl = null;
      if (photo) {
        photoUrl = await uploadPhoto(photo);
      }

      const { error } = await supabase
        .from('community_reports')
        .insert({
          farmer_id: user.id,
          farmer_name: user.user_metadata?.full_name || user.name || 'Anonymous Farmer',
          village: formData.village,
          crop: formData.crop,
          report_type: formData.reportType,
          severity: formData.severity,
          description: formData.description,
          photo_url: photoUrl,
          gps_coords: gpsCoords,
          confidence: photo ? 75 : 60 // Higher confidence with photo
        });

      if (error) throw error;

      onReportCreated();
      onClose();
      
      // Reset form
      setFormData({
        village: "",
        crop: "maize",
        reportType: "pest",
        severity: "medium",
        description: ""
      });
      setPhoto(null);
      setPhotoPreview("");
      setGpsCoords(null);
    } catch (error) {
      console.error('Report creation failed:', error);
      alert('Failed to create report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-800">Quick Report</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Village/Location</label>
            <input
              type="text"
              required
              value={formData.village}
              onChange={(e) => setFormData({...formData, village: e.target.value})}
              placeholder="e.g., Ha Abia, Maseru"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Crop</label>
            <select
              value={formData.crop}
              onChange={(e) => setFormData({...formData, crop: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            >
              <option value="maize">Maize</option>
              <option value="beans">Beans</option>
              <option value="sorghum">Sorghum</option>
              <option value="wheat">Wheat</option>
              <option value="vegetables">Vegetables</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
            <select
              value={formData.reportType}
              onChange={(e) => setFormData({...formData, reportType: e.target.value as any})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            >
              <option value="pest">Pest Issue</option>
              <option value="disease">Disease</option>
              <option value="yield">Yield Data</option>
              <option value="weather">Weather Event</option>
              <option value="technique">Farming Technique</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
            <select
              value={formData.severity}
              onChange={(e) => setFormData({...formData, severity: e.target.value as any})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              required
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Describe what you observed..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Photo (Optional)</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Camera className="w-4 h-4" />
                <span>Add Photo</span>
              </button>
              <button
                type="button"
                onClick={getLocation}
                className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <MapPin className="w-4 h-4" />
                <span>Get GPS</span>
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              className="hidden"
            />
            {photoPreview && (
              <img src={photoPreview} alt="Preview" className="mt-2 w-full h-32 object-cover rounded-lg" />
            )}
            {gpsCoords && (
              <p className="text-xs text-green-600 mt-1">GPS: {gpsCoords.lat.toFixed(4)}, {gpsCoords.lng.toFixed(4)}</p>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Report"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}